import { useState } from 'react'
import { useQuoteApi } from 'src/api/db/apiQuote'
import { useOrderNotifications } from 'src/api/server/useOrderNotifications'
import { Column } from 'src/shared/components/Semantic/all'
import {
  APPROVED_QUOTE_STATUS,
  SALES_APPROVED_QUOTE_STATUS,
  effectiveQuoteStatus,
  isAwaitingKlayderReview,
  isPreSalesApprovalStatus,
} from 'src/shared/consts/quoteStatus'
import { useAuth } from 'src/shared/hooks/authProvider'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { useToast } from 'src/shared/hooks/use-toast'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { buildAppRequestPath } from 'src/routes/paths'
import { useRequestContext } from '../../../../hooks/RequestContext'
import { GuestSignupDialog } from '../../../Chat/components/GuestSignupDialog'
import { BillingDocument } from '../../../BillingDocument'
import { QuoteApprovalParties } from './QuoteApprovalParties'

export const ApproveQuoteStep = ({
  onApproved,
}: {
  onApproved?: () => void
}) => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const { session } = useAuth()
  const isAdmin = useIsAdmin()
  const { publicId, quote, quoteItems, refetchQuote, isDemo, onDemoAdvance } =
    useRequestContext()
  const [signupOpen, setSignupOpen] = useState(false)
  const isAnonymous = !isDemo && !session?.user?.email
  const { updateQuote, approveSharedQuote } = useQuoteApi({
    skipListQuery: true,
  })
  const orderNotifications = useOrderNotifications()

  const quoteId = quote?.id
  const quotePublicId = quote?.public_id ?? publicId
  const status = effectiveQuoteStatus(quote?.status)
  const hasItems = (quoteItems?.length ?? 0) > 0
  const awaitingKlayder = isAwaitingKlayderReview(status)
  const klayderApproved = !isPreSalesApprovalStatus(status)
  const customerApproved =
    klayderApproved && status !== SALES_APPROVED_QUOTE_STATUS
  const canKlayderPress =
    isAdmin && !isDemo && !klayderApproved && hasItems
  const canCustomerPress =
    !isAdmin && klayderApproved && !customerApproved && hasItems

  const handleSalesApprove = async () => {
    if (isDemo || !quoteId) return
    try {
      const result = await updateQuote.mutateAsync({
        id: quoteId,
        status: SALES_APPROVED_QUOTE_STATUS,
      })
      if (result.error) throw new Error(result.error.message)
      if (quotePublicId) {
        await orderNotifications.mutateAsync({
          kind: 'lifecycle',
          actorRole: 'admin',
          subject:
            t(
              'Quote ready to approve for order ',
              'Offert klar att godkänna för order '
            ) + `#${quotePublicId}`,
          orderNotification: {
            quotePublicId,
            headerBadge: t('Ready to approve', 'Klar att godkänna'),
            headerTitle: t(
              'Your quote is ready to approve',
              'Din offert är klar att godkänna'
            ),
            message: t(
              'Klayder has released your quote. Open Checkout to review pricing and approve when you are ready.',
              'Klayder har släppt din offert. Öppna kassan för att granska priset och godkänna när du är redo.'
            ),
            buttonText: t('View order', 'Se order'),
          },
        })
      }
      refetchQuote()
      toast({
        title: t('Quote released to customer', 'Offert skickad till kund'),
        description: t(
          'The customer can now approve the quote in Checkout.',
          'Kunden kan nu godkänna offerten i kassan.'
        ),
      })
    } catch (error) {
      console.error('Failed to release quote:', error)
      toast({
        variant: 'destructive',
        title: t('Something went wrong', 'Något gick fel'),
        description: t(
          'Could not release the quote. Please try again.',
          'Kunde inte skicka offerten. Försök igen.'
        ),
      })
    }
  }

  const handleCustomerApprove = async () => {
    if (isDemo) {
      onDemoAdvance?.('approve_quote')
      onApproved?.()
      return
    }
    if (!quoteId || !quotePublicId) return
    try {
      await approveSharedQuote.mutateAsync({
        public_id: quotePublicId,
        quote_id: quoteId,
      })
      if (quotePublicId) {
        await orderNotifications.mutateAsync({
          kind: 'lifecycle',
          actorRole: 'customer',
          subject:
            t('Quote approved for order ', 'Offert godkänd för order ') +
            `#${quotePublicId}`,
          orderNotification: {
            quotePublicId,
            headerBadge: t('Quote approved', 'Offert godkänd'),
            headerTitle: t(
              'Customer approved the quote',
              'Kunden har godkänt offerten'
            ),
            message: t(
              'The customer approved the quote and requested factory proofs. Click below to view the order.',
              'Kunden har godkänt offerten och begärt korrekturer. Klicka nedan för att visa ordern.'
            ),
            buttonText: t('View order', 'Se order'),
          },
        })
      }
      refetchQuote()
      onApproved?.()
    } catch (error) {
      console.error('Failed to approve quote:', error)
      toast({
        variant: 'destructive',
        title: t('Something went wrong', 'Något gick fel'),
        description: t(
          'Could not approve the quote. Please try again.',
          'Kunde inte godkänna offerten. Försök igen.'
        ),
      })
    }
  }

  if (!quote) return null

  return (
    <Column className="gap-4">
      <BillingDocument
        quote={quote}
        quoteItems={quoteItems}
        publicId={publicId}
        mode="quote"
        isReadOnly={isDemo}
        embedded
      />

      <QuoteApprovalParties
        isAdmin={isAdmin}
        klayderApproved={klayderApproved}
        customerApproved={customerApproved}
        canKlayderPress={canKlayderPress}
        canCustomerPress={canCustomerPress}
        awaitingKlayder={awaitingKlayder}
        isAnonymous={isAnonymous}
        onKlayderApprove={handleSalesApprove}
        onCustomerApprove={
          isAnonymous ? () => setSignupOpen(true) : handleCustomerApprove
        }
        isKlayderLoading={canKlayderPress && updateQuote.isPending}
        isCustomerLoading={
          canCustomerPress && !isAnonymous && approveSharedQuote.isPending
        }
      />

      {isAnonymous && quotePublicId ? (
        <GuestSignupDialog
          open={signupOpen}
          onOpenChange={setSignupOpen}
          initialEmail=""
          nextPath={buildAppRequestPath(quotePublicId, 'checkout')}
          title={t('Sign up to continue', 'Skapa konto för att fortsätta')}
          description={t(
            'To continue the conversation and complete the purchase, please create a free account on your work email.',
            'För att fortsätta konversationen och slutföra köpet, skapa ett gratiskonto på din arbetsmejl.'
          )}
        />
      ) : null}
    </Column>
  )
}
